import { useR2DispatchContext, useR2Highlights, useR2StateContext } from '../../../../providers/r2Provider/R2Context'

/**
 * Hook for managing person and record highlights in both the list UI and image viewer.
 *
 * This hook provides two types of highlight management:
 * 1. List visual state (gray background on list items) - controlled by R2 state
 * 2. Image highlights (yellow boxes on person names in image) - controlled by highlight APIs
 *
 * These concerns are intentionally separated to allow image highlights to persist
 * when the user moves their mouse from the list to the image viewer.
 *
 * @returns {Object} Highlight management functions
 */
export default function useHoverHighlights() {
  const r2Dispatch = useR2DispatchContext()
  const r2State = useR2StateContext()

  const {
    displayHighlights,
    displayHighlightsApi,
    recordDisplayHighlights,
    recordDisplayHighlightsApi,
    splitScreenDisplayHighlights,
    splitScreenDisplayHighlightsApi,
    splitScreenRecordDisplayHighlights,
    splitScreenRecordDisplayHighlightsApi,
  } = useR2Highlights()

  /**
   * Internal helper that sets both list state AND image highlights.
   * @private
   */
  const setHoveredHighlights = ({ api, value, highlightId = '' }) => {
    r2Dispatch({ type: 'SET_STATE', value })
    api.hover(highlightId)
  }

  /**
   * Highlights a person's name in both the list and the image viewer.
   * Updates both list visual state and image highlight overlays.
   *
   * Use this when the user hovers over a person in the list (onMouseEnter).
   *
   * @param {Object} params
   * @param {Object} params.person - The person object to highlight
   */
  const highlightPersonNameHighlight = ({ person }) => {
    const highlightsContainingPersonId = displayHighlights.ids.filter((id) => {
      return displayHighlights.highlights[id]?.personIds?.includes(person?.id)
    })
    if (highlightsContainingPersonId.length) {
      setHoveredHighlights({
        api: displayHighlightsApi,
        value: { highlightedPersonId: person?.id },
        highlightId: highlightsContainingPersonId,
        r2Dispatch,
      })
    }

    // Also highlight on split screen (or clear if no highlights)
    if (r2State.splitScreen) {
      const splitScreenHighlightsContainingPersonId = splitScreenDisplayHighlights.ids.filter((id) => {
        return splitScreenDisplayHighlights.highlights[id]?.personIds?.includes(person?.id)
      })
      splitScreenDisplayHighlightsApi.hover(splitScreenHighlightsContainingPersonId)
    }
  }

  /**
   * Highlights a record in the image viewer.
   * Updates image highlight overlays for the record.
   *
   * Use this when the user hovers over a record in the list (onMouseEnter).
   *
   * @param {Object} params
   * @param {Object} params.record - The record object to highlight
   */
  const highlightRecordHighlight = ({ record }) => {
    const highlightContainingRecordId = recordDisplayHighlights.ids.find((id) => id === record?.id)
    // don't update state here so that only the person is highlighted and not the record
    if (highlightContainingRecordId) {
      setHoveredHighlights({
        api: recordDisplayHighlightsApi,
        value: {},
        highlightId: [highlightContainingRecordId],
        r2Dispatch,
      })
    }

    // Also highlight on split screen (or clear if no highlights)
    if (r2State.splitScreen) {
      const splitScreenHighlightContainingRecordId = splitScreenRecordDisplayHighlights.ids.find(
        (id) => id === record?.id
      )
      const highlightIds = splitScreenHighlightContainingRecordId ? [splitScreenHighlightContainingRecordId] : []
      splitScreenRecordDisplayHighlightsApi.hover(highlightIds)
    }
  }

  /**
   * Clears person highlights in BOTH the list AND the image viewer.
   * This removes both the list visual state and the image highlight overlays.
   *
   * Use this when you want to completely clear all person highlights, such as:
   * - When closing a panel
   * - When navigating away from the page
   * - When explicitly clearing all highlights
   *
   * DO NOT use this in onMouseLeave handlers - use resetPersonListHighlight instead.
   */
  const resetPersonNameHighlights = () => {
    setHoveredHighlights({ api: displayHighlightsApi, value: { highlightedPersonId: '' } })
    if (r2State.splitScreen) {
      splitScreenDisplayHighlightsApi.hover([])
    }
  }

  /**
   * Clears record highlights in BOTH the list AND the image viewer.
   * This removes both the list visual state and the image highlight overlays.
   *
   * Use this when you want to completely clear all record highlights.
   *
   * DO NOT use this in onMouseLeave handlers - use resetRecordListHighlight instead.
   */
  const resetRecordHighlights = () => {
    setHoveredHighlights({ api: recordDisplayHighlightsApi, value: { highlightedRecordId: '' } })
    if (r2State.splitScreen) {
      splitScreenRecordDisplayHighlightsApi.hover([])
    }
  }

  /**
   * Clears person highlight in the LIST ONLY (not the image viewer).
   * This removes the gray background from the list item but preserves the yellow
   * highlight boxes on the image, allowing users to move their mouse from the list
   * to the image while keeping the highlight visible.
   *
   * Use this in onMouseLeave handlers when the user's mouse leaves a list item.
   *
   * The image viewer has its own hover event listeners that will clear the image
   * highlights when appropriate (e.g., when hovering over a different area).
   */
  const resetPersonListHighlight = () => {
    r2Dispatch({ type: 'SET_STATE', value: { highlightedPersonId: '' } })
  }

  /**
   * Clears record highlight in the LIST ONLY (not the image viewer).
   * This removes the visual highlight from the list but preserves image highlights.
   *
   * Use this in onMouseLeave handlers when the user's mouse leaves a record list item.
   */
  const resetRecordListHighlight = () => {
    r2Dispatch({ type: 'SET_STATE', value: { highlightedRecordId: '' } })
  }

  /**
   * Checks if a record or person is currently highlighted.
   *
   * @param {Object} params
   * @param {Object} params.record - The record object to check
   * @param {Object} params.person - The person object to check
   * @returns {boolean} True if the record or person is highlighted
   */
  const getIsSelected = ({ record, person }) => {
    if (!record && !person) return false
    const isRecordHighlighted = Boolean(r2State?.highlightedRecordId && r2State.highlightedRecordId === record?.id)
    const isPersonSelected = Boolean(r2State?.highlightedPersonId && r2State.highlightedPersonId === person?.id)
    return isRecordHighlighted || isPersonSelected
  }

  return {
    // Highlight setters (use in onMouseEnter)
    highlightPersonNameHighlight,
    highlightRecordHighlight,

    // Full reset functions (clear both list AND image highlights)
    // Use when completely clearing highlights (e.g., closing panels, navigation)
    resetPersonNameHighlights,
    resetRecordHighlights,

    // List-only reset functions (clear list visual state, preserve image highlights)
    // Use in onMouseLeave handlers to allow users to move mouse from list to image
    resetPersonListHighlight,
    resetRecordListHighlight,

    // State checker
    getIsSelected,
  }
}
